import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { ENROLLMENT_YEAR, documentConfigs } from "./documentConfig";
import { loadEnrollmentDocument, saveEnrollmentDocument } from "./enrollmentStorage";
import EnrollmentFields from "./EnrollmentFields";
import { formatEnrollmentDate } from "../../utils/enrollmentDate";
import "./documentEditor.css";
import "./enrollmentFields.css";

const DNI_REUSE_START_YEAR = 2027;
const getEnteredDni = (value) => String(value || "").trim();

export function DocumentPreviewPage({ page, pageIndex, fields, values, showValueBackground }) {
  const pageRef = useRef(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const updateScale = () => {
      if (pageRef.current) setScale(pageRef.current.clientWidth / page.width);
    };

    updateScale();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateScale);
    if (pageRef.current && observer) observer.observe(pageRef.current);
    window.addEventListener("resize", updateScale);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [page.width]);

  return (
    <div className="document-editor__page" ref={pageRef}>
      <img
        src={page.imageUrl}
        alt={`Página ${pageIndex + 1} del documento`}
        onLoad={() => setScale(pageRef.current ? pageRef.current.clientWidth / page.width : 0)}
      />
      {scale > 0 &&
        (page.previewOverlays || page.overlays)?.map((overlay) => (
          <span
            aria-hidden="true"
            className="document-editor__static-overlay"
            key={overlay.id}
            style={{
              left: `${overlay.x * scale}px`,
              top: `${overlay.top * scale}px`,
              width: `${overlay.width * scale}px`,
              height: `${overlay.height * scale}px`,
              fontFamily: overlay.fontFamily,
              fontSize: `${overlay.fontSize * scale}px`,
              fontWeight: overlay.fontWeight,
              WebkitTextStroke:
                Number(overlay.fontWeight) > 700
                  ? `${
                      Math.min(1, (Number(overlay.fontWeight) - 700) / 500) * scale
                    }px currentColor`
                  : undefined,
              background: overlay.clear ? "#fff" : "transparent",
              borderTop:
                overlay.type === "line"
                  ? `${Math.max(1, (overlay.thickness || 1) * scale)}px ${
                      overlay.dashArray ? "dashed" : "solid"
                    } #000`
                  : undefined,
            }}
          >
            {overlay.text}
          </span>
        ))}
      {scale > 0 &&
        fields
          .filter((field) => field.page === pageIndex)
          .map((field) => {
            const fieldStyle = {
              left: `${field.x * scale}px`,
              top: `${field.top * scale}px`,
              width: `${field.width * scale}px`,
              height: `${field.height * scale}px`,
              fontSize: `${(field.fontSize || 9) * scale}px`,
              fontWeight: field.fontWeight || 400,
              textAlign: field.align || "left",
              background: field.previewClear ? "#fff" : undefined,
            };

            if (field.type === "checkbox") {
              return (
                <label
                  className="document-editor__checkbox"
                  style={fieldStyle}
                  key={field.id}
                  title={field.label}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(values[field.id])}
                    disabled
                    aria-label={field.label}
                  />
                  <span aria-hidden="true">×</span>
                </label>
              );
            }

            const fieldValue = values[field.id];
            const value =
              typeof fieldValue === "string"
                ? field.id.includes("date")
                  ? formatEnrollmentDate(fieldValue)
                  : fieldValue
                : "";

            if (showValueBackground) {
              return (
                <span
                  aria-label={field.label}
                  className={`document-editor__preview-value${
                    field.variant ? ` document-editor__preview-value--${field.variant}` : ""
                  }`}
                  key={field.id}
                  style={fieldStyle}
                  title={field.label}
                >
                  {value}
                </span>
              );
            }

            return (
              <input
                className={`document-editor__field${
                  field.variant ? ` document-editor__field--${field.variant}` : ""
                }`}
                style={fieldStyle}
                type="text"
                key={field.id}
                value={value}
                readOnly
                tabIndex={-1}
                aria-label={field.label}
                title={field.label}
                placeholder=" "
                autoComplete="off"
              />
            );
          })}
    </div>
  );
}

export default function DirectDocumentEditor({
  document: documentType,
  backHref = "/formularios",
  backLabel = "← Volver a los documentos",
  versionLabel = "",
  documentConfig,
  renderPreview,
  generatePdf,
  exportDocument,
  pdfFieldOffsets,
  downloadAsA4 = false,
  extraDocumentFields = [],
  hiddenFieldIds,
  resolveDocumentValues,
  loadRelatedValues,
  showAnexoAuthorizationInfo,
}) {
  const config = documentConfig || documentConfigs[documentType];
  const isDniReuseEnabled = ENROLLMENT_YEAR >= DNI_REUSE_START_YEAR;
  const [values, setValues] = useState({});
  const [downloadType, setDownloadType] = useState("");
  const [error, setError] = useState("");
  const [isLookingUpDni, setIsLookingUpDni] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [step, setStep] = useState("form");
  const stepHeading = useRef(null);
  const busy = Boolean(downloadType) || isLookingUpDni || saveStatus === "saving";
  const formDisabled = Boolean(downloadType) || saveStatus === "saving";
  const documentValues = resolveDocumentValues ? resolveDocumentValues(values) : values;
  const outputConfig = {
    ...config,
    fields: [...config.fields, ...extraDocumentFields],
  };

  useEffect(() => {
    stepHeading.current?.focus();
  }, [step]);

  useEffect(() => {
    window.document.title = config.title;
  }, [config.title]);

  const updateField = (field, value) => {
    setSaveStatus("");
    setValues((current) => {
      if (field.type === "checkbox" && field.group && value) {
        const next = { ...current, [field.id]: value };
        config.fields.forEach((candidate) => {
          if (candidate.id !== field.id && candidate.group === field.group)
            next[candidate.id] = false;
        });
        return next;
      }

      return { ...current, [field.id]: value };
    });
  };

  const loadSavedValues = async (dniFromInput) => {
    const dni = getEnteredDni(dniFromInput ?? values.student_dni);
    const valuesAtLookupStart = { ...values, student_dni: dni };
    if (!isDniReuseEnabled || isLookingUpDni) return;
    if (!dni) return;

    setIsLookingUpDni(true);
    setError("");
    setSaveStatus("");

    try {
      const storedValues = await loadEnrollmentDocument(dni, documentType);
      let relatedValues = {};
      if (loadRelatedValues) {
        try {
          relatedValues = await loadRelatedValues(dni);
        } catch (relatedLoadError) {
          console.error(relatedLoadError);
        }
      }
      const valuesToLoad = { ...storedValues, ...relatedValues, student_dni: dni };
      if (Object.keys(valuesToLoad).length) {
        setValues((current) => {
          const changesMadeDuringLookup = Object.fromEntries(
            Object.entries(current).filter(
              ([fieldId, value]) => value !== valuesAtLookupStart[fieldId]
            )
          );

          return { ...valuesToLoad, ...changesMadeDuringLookup };
        });
        setSaveStatus("loaded");
      } else {
        setSaveStatus("not-found");
      }
    } catch (loadError) {
      console.error(loadError);
      setError(
        "No pudimos recuperar los datos guardados. Podés completar el documento normalmente."
      );
    } finally {
      setIsLookingUpDni(false);
    }
  };

  const saveValues = async () => {
    const dni = getEnteredDni(values.student_dni);
    if (!dni) return false;
    setError("");
    setSaveStatus("saving");
    try {
      await saveEnrollmentDocument(dni, documentType, values);
      setSaveStatus("saved");
      return true;
    } catch (saveError) {
      console.error(saveError);
      setSaveStatus("error");
      return false;
    }
  };

  const downloadPdf = async (type) => {
    const dni = getEnteredDni(values.student_dni);

    setDownloadType(type);
    setError("");

    try {
      if (type === "completed" && isDniReuseEnabled && dni) await saveValues();

      if (exportDocument) {
        await exportDocument(documentType, type === "completed" ? documentValues : {});
        return;
      }

      let pdfBytes;
      if (generatePdf) {
        pdfBytes = await generatePdf(documentType, type === "completed" ? documentValues : {});
      } else {
        const response = await fetch(config.pdfUrl);
        if (!response.ok) throw new Error("No se pudo cargar el PDF original.");
        const source = await response.arrayBuffer();
        const { buildCompletedDocumentPdf } = await import("utils/generateCompletedDocumentPdf");
        pdfBytes = await buildCompletedDocumentPdf(
          source,
          outputConfig,
          type === "completed" ? documentValues : {},
          pdfFieldOffsets
        );
      }
      if (downloadAsA4) {
        const { convertPdfToA4 } = await import("utils/generateCompletedDocumentPdf");
        pdfBytes = await convertPdfToA4(pdfBytes);
      }
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = type === "completed" ? config.completedPdfName : config.emptyPdfName;
      link.style.display = "none";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (downloadError) {
      console.error(downloadError);
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : "No se pudo generar el PDF. Intentá nuevamente desde un navegador actualizado."
      );
    } finally {
      setDownloadType("");
    }
  };

  return (
    <main
      className={`document-editor${versionLabel === "V2" ? " document-editor--v2" : ""}`}
      id={`editor-${documentType}`}
    >
      <header className="document-editor__toolbar">
        <div className="document-editor__toolbar-intro">
          <Link className="document-editor__back" to={backHref}>
            {backLabel}
          </Link>
          <p className="document-editor__eyebrow">
            Inscripción {ENROLLMENT_YEAR}
            {versionLabel ? ` · ${versionLabel}` : ""}
          </p>
          <h1>{config.heading}</h1>
          <p>Completá el formulario y revisá el documento antes de descargarlo.</p>
        </div>
        <div className="document-editor__actions" aria-label="Opciones del documento">
          <Button
            type="button"
            disabled={busy}
            onClick={() => downloadPdf("empty")}
            variant="outlined"
            color="inherit"
            startIcon={
              downloadType === "empty" ? <CircularProgress color="inherit" size={17} /> : null
            }
          >
            {downloadType === "empty" ? "Generando PDF vacío…" : "Descargar PDF vacío"}
          </Button>
        </div>
      </header>

      {error && (
        <p className="document-editor__error" role="alert">
          {error}
        </p>
      )}
      <p className="document-editor__instructions">
        Completá solo los datos que correspondan. El PDF conserva el formato original. Las firmas
        deben realizarse a mano luego de imprimirlo.
      </p>

      {saveStatus && (
        <p
          className={`document-editor__save-status document-editor__save-status--${saveStatus}`}
          role={saveStatus === "error" ? "alert" : "status"}
        >
          {saveStatus === "saving" && "Guardando cambios…"}
          {saveStatus === "saved" && "Cambios guardados."}
          {saveStatus === "loaded" && "Recuperamos los datos guardados para este DNI."}
          {saveStatus === "not-found" &&
            "No hay datos guardados para este DNI en este documento. Podés completar el formulario."}
          {saveStatus === "error" &&
            "No se pudieron guardar los cambios. Revisá la conexión e intentá de nuevo."}
        </p>
      )}

      <div className="enrollment-fields__steps" aria-label="Paso actual">
        <h2 ref={stepHeading} tabIndex={-1}>
          {step === "form" ? "1. Completá los datos" : "2. Revisá el documento"}
        </h2>
        <p>
          {step === "form"
            ? "Después vas a poder revisar cómo quedan en el documento."
            : "Para corregir algún dato, volvé al formulario."}
        </p>
      </div>

      {step === "form" ? (
        <form
          className="enrollment-fields"
          onSubmit={(event) => {
            event.preventDefault();
            setStep("preview");
          }}
        >
          <EnrollmentFields
            documentType={documentType}
            fields={config.fields}
            values={values}
            onChange={updateField}
            onStudentDniBlur={isDniReuseEnabled ? loadSavedValues : undefined}
            disabled={formDisabled}
            hiddenFieldIds={hiddenFieldIds}
            showAnexoAuthorizationInfo={showAnexoAuthorizationInfo}
          />
          <div className="enrollment-fields__actions">
            <Button type="submit" variant="contained" disabled={busy}>
              Continuar a la vista previa
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="enrollment-fields__actions">
            <Button
              type="button"
              variant="outlined"
              disabled={formDisabled}
              onClick={() => setStep("form")}
            >
              Volver al formulario
            </Button>
          </div>
          <section
            className="document-editor__viewport document-editor__viewport--preview"
            aria-label={`Vista previa de ${config.heading}`}
          >
            {renderPreview
              ? renderPreview({ documentType, values: documentValues })
              : config.pages.map((page, pageIndex) => (
                  <DocumentPreviewPage
                    key={page.imageUrl}
                    page={page}
                    pageIndex={pageIndex}
                    fields={outputConfig.fields}
                    values={documentValues}
                    showValueBackground={documentType === "anexo"}
                  />
                ))}
          </section>

          <Button
            className="document-editor__floating-download"
            type="button"
            disabled={busy}
            onClick={() => downloadPdf("completed")}
            variant="contained"
            color="primary"
            aria-label="Descargar PDF"
            startIcon={
              downloadType === "completed" ? <CircularProgress color="inherit" size={17} /> : null
            }
          >
            {downloadType === "completed" ? "Generando PDF…" : "Descargar PDF"}
          </Button>
        </>
      )}
    </main>
  );
}
